import {Component} from "vue";
import {FilterField, OperatorOption, WidgetsPresenter} from "@/utils/presenters/interfaces.ts";
import EditScreenFieldUtils from "@/utils/editView/editScreens/EditScreenFieldUtils.ts";
import FieldTypesUtils from "@/utils/common/FieldTypesUtils.ts";
import CommonConstants from "@/utils/constants/CommonConstants.ts";
import Translator from "@/utils/common/Translator.ts";
import Operator from "@/utils/constants/Operator.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import {QuestionFieldsPresenter} from "@/utils/presenters/QuestionFieldsPresenter.ts";
import ListDisplayType from "@/utils/constants/ListDisplayType.ts";
import ChipsFieldWidget from "@/components/questionFields/widgets/ChipsFieldWidget.vue";
import DateRangeFieldWidget from "@/components/EditView/widgets/DateRangeFieldWidget.vue";
import QuestionField = namaTypes.QuestionField;
import FieldMetaData = namaTypes.FieldMetaData;
import CriteriaField = namaTypes.CriteriaField;
import AllowedValue = namaTypes.AllowedValue;

export default class ExpressionEditUtility {

  static getParsedValue(filterField: QuestionField | CriteriaField, rightHandSideValue: string | undefined, inValues: string[] | undefined): any {
    // viewNameProvider: ActionPerformer
    if (!filterField)
      return;
    let parsedValue: any = FieldTypesUtils.parseString(rightHandSideValue, filterField.metaData?.fieldType);
    if (parsedValue == CommonConstants.DEFAULT_VALUE_CURRENT_VIEW_NAME) {
      // if (viewNameProvider)
      //   parsedValue = viewNameProvider.currentViewName();
      // else
      parsedValue = "";
    }
    return parsedValue;
  }

  static getWidget(filterField: FilterField, presenter: WidgetsPresenter): Component | undefined {
    if (!filterField || !filterField.metaData)
      return;
    if (filterField.hijri)
      filterField.metaData.hijri = filterField.hijri;
    if (filterField.allowedValues) {
      filterField.metaData.allowedValues = filterField.allowedValues.map(av => av.id ?? av.value ?? "");
      filterField.metaData.allowedValuesTitles = filterField.allowedValues;
    }
    if (filterField.metaData.enumTypeName === "SubsidiaryType" && filterField.metaData.allowedValues) {
      let typesShownInReports = this.fetchSubsidiaryTypesShownInReports(presenter);
      filterField.metaData.allowedValues = filterField.metaData.allowedValues?.filter(av => this.isSubsidiaryTypeShown(av, typesShownInReports));
      if (filterField.metaData.allowedValuesTitles)
        filterField.metaData.allowedValuesTitles = filterField.metaData.allowedValuesTitles.filter(av => this.isSubsidiaryTypeShown(av.value ?? av.id, typesShownInReports));
    }
    if (filterField.list)
      filterField.metaData.list = filterField.list;
    if (filterField.doNotAutoShowList)
      filterField.metaData.doNotAutoShowList = filterField.doNotAutoShowList;
    let fieldMetadata: FieldMetaData = filterField.metaData as FieldMetaData;
    fieldMetadata.required = filterField.required;
    fieldMetadata.fieldId = filterField.id;
    if (filterField && (presenter instanceof QuestionFieldsPresenter)) {
      if (filterField.listDisplayType === ListDisplayType.Chips)
        return ChipsFieldWidget;
      else if (filterField.showAsDateRange)
        return DateRangeFieldWidget;
    }
    return EditScreenFieldUtils.createField(presenter, fieldMetadata);
  }

  static fetchSubsidiaryTypesShownInReports(presenter: WidgetsPresenter): string[] {
    if (presenter instanceof QuestionFieldsPresenter && presenter.reportMetadata)
      return ClientCache.getReportsSubsidiaryTypes();
    return [];
  }

  static isSubsidiaryTypeShown(subsidiaryType: string | undefined, typesShownInReports: string[]): boolean {
    if (!ClientCache.isEntityEnabled(subsidiaryType))
      return false;
    return !typesShownInReports.length || typesShownInReports.includes(subsidiaryType!);
  }

  static getAvailableOperatorsAsOptions(id: string | null | undefined, availableFilters: CriteriaField[] | null | undefined) {
    let defaultReturn: OperatorOption[] = [
      this.createOperatorOption(Operator.Equal),
      this.createOperatorOption(Operator.NotEqual),
    ];
    if (!id || !availableFilters || !availableFilters.length)
      return defaultReturn;
    let matchedField = availableFilters.find(l => l.id === id);
    if (!matchedField || !matchedField.metaData || !matchedField.metaData.fieldType || matchedField.allowedValues)
      return defaultReturn;
    if ((matchedField.allowedValues && (matchedField.allowedValues as AllowedValue[]).length) || matchedField.metaData.fieldType === FieldType.Enum
      || matchedField.metaData.fieldType === FieldType.EntityType)
      return defaultReturn;
    let fieldType = matchedField.metaData.fieldType;
    switch (fieldType) {
      case FieldType.Text:
        return [
          this.createOperatorOption(Operator.Equal),
          this.createOperatorOption(Operator.NotEqual),
          this.createOperatorOption(Operator.StartsWith),
          this.createOperatorOption(Operator.NotStartsWith),
          this.createOperatorOption(Operator.EndsWith),
          this.createOperatorOption(Operator.NotEndWith),
          this.createOperatorOption(Operator.Contains),
          this.createOperatorOption(Operator.NotContain),
          this.createOperatorOption(Operator.ContainsWithAnyOrder),
          this.createOperatorOption(Operator.GreaterThan),
          this.createOperatorOption(Operator.GreaterThanOrEqual),
          this.createOperatorOption(Operator.LessThan),
          this.createOperatorOption(Operator.LessThanOrEqual),
          this.createOperatorOption(Operator.In),
          this.createOperatorOption(Operator.NotIn),
        ];
      case FieldType.BigText:
      case FieldType.Link:
      case FieldType.Email:
      case FieldType.FieldID:
      case FieldType.FeatureID:
      case FieldType.LongToken:
      case FieldType.PhoneNumber:
      case FieldType.Password:
      case FieldType.LatLng:
      case FieldType.Color:
        return [
          this.createOperatorOption(Operator.Equal),
          this.createOperatorOption(Operator.NotEqual),
          this.createOperatorOption(Operator.StartsWith),
          this.createOperatorOption(Operator.NotStartsWith),
          this.createOperatorOption(Operator.EndsWith),
          this.createOperatorOption(Operator.NotEndWith),
          this.createOperatorOption(Operator.Contains),
          this.createOperatorOption(Operator.NotContain),
          this.createOperatorOption(Operator.ContainsWithAnyOrder),
          this.createOperatorOption(Operator.GreaterThan),
          this.createOperatorOption(Operator.GreaterThanOrEqual),
          this.createOperatorOption(Operator.LessThan),
          this.createOperatorOption(Operator.LessThanOrEqual)
        ];
      case FieldType.Integer:
      case FieldType.Decimal:
      case FieldType.Long:
      case FieldType.Time:
        return [
          this.createOperatorOption(Operator.Equal),
          this.createOperatorOption(Operator.NotEqual),
          this.createOperatorOption(Operator.GreaterThan),
          this.createOperatorOption(Operator.GreaterThanOrEqual),
          this.createOperatorOption(Operator.LessThan),
          this.createOperatorOption(Operator.LessThanOrEqual),
          this.createOperatorOption(Operator.In),
          this.createOperatorOption(Operator.NotIn),
        ];
      case FieldType.DateAndTime:
      case FieldType.Date:
        return [
          this.createOperatorOption(Operator.Equal),
          this.createOperatorOption(Operator.NotEqual),
          this.createOperatorOption(Operator.GreaterThan),
          this.createOperatorOption(Operator.GreaterThanOrEqual),
          this.createOperatorOption(Operator.LessThan),
          this.createOperatorOption(Operator.LessThanOrEqual),
          this.createOperatorOption(Operator.WithinPeriod),
          this.createOperatorOption(Operator.OutsidePeriod)
        ];
      case FieldType.Boolean:
        return [this.createOperatorOption(Operator.Equal)];
      case FieldType.Reference:
        return [
          this.createOperatorOption(Operator.Equal),
          this.createOperatorOption(Operator.NotEqual),
          this.createOperatorOption(Operator.In),
          this.createOperatorOption(Operator.NotIn),
        ];
      case FieldType.EntityType:
      case FieldType.Enum:
        return defaultReturn;
    }
    return defaultReturn;
  }

  static createOperatorOption(operator: string): OperatorOption {
    return {
      label: Translator.translateId(operator,'Operator'),
      value: operator,
      searchValue: EditScreenFieldUtils.fetchSearchTextForAllowedValue(operator,'Operator')
    };
  }
}
